import { useAllPricelists, useGeneratePricelistPrintReport } from "http-hooks/pricelist/usePricelist";
import DeletePricelist from "components/pricelist/DeletePricelist";
import { CheckIcon, XMarkIcon } from "@heroicons/react/20/solid";
import { useState, useMemo } from "react";
import useModalStore from "store/modals";
import Input from "components/ui/Input";
import Select from "components/ui/Select";
import { SelectVendors } from "components/ui/selectors";
import useAuth from "hooks/useAuth";
import { ActionButton } from "components/ui/Table";
import { PrinterIcon } from "@heroicons/react/24/outline";
import { useNavigate } from "react-router-dom";
import { getDocumentUrls, getDocumentViewerRoute } from "utils/documentUrlHelper";
import MasterPage, {
  FilterRow,
  FilterCell,
  ClearFiltersButton,
  AddButton,
  EditAction,
  DeleteAction,
} from "components/ui/MasterPage";

const statusOptions = [
  { value: "", label: "All Status" },
  { value: "1", label: "Active" },
  { value: "0", label: "Inactive" },
];

const typeLabels = {
  G: "General",
  P: "Purchase",
  S: "Sales",
};

const initialFilter = {
  from_cirdt: "",
  to_cirdt: "",
  from_effrctivedt: "",
  to_effrctivedt: "",
  supid: "",
  ag_status: "",
  page: 1,
  limit: 10,
};

const showDateInWords = (input) => {
  const date = new Date(input);
  const months = [
    "Jan", "Feb", "Mar", "Apr", "May", "Jun",
    "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
  ];
  const day = date.getDate();
  const monthIndex = date.getMonth();
  const year = date.getFullYear();
  return `${day}-${months[monthIndex]}-${year}`;
};

export default function PricelistMaster() {
  const { agttp } = useAuth();
  const isTrader = agttp === 1;
  const navigate = useNavigate();

  const [deleteItemId, setDeleteItemId] = useState(null);
  const [printingId, setPrintingId] = useState(null);
  const [filter, setFilter] = useState(initialFilter);
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState(null);
  const { openModal } = useModalStore();

  const { mutateAsync: generateReport } = useGeneratePricelistPrintReport();

  const openSetModal = (id) => {
    openModal("pricelist", id);
  };

  const handlePrintReport = async (rateid) => {
    try {
      setPrintingId(rateid);
      const data = await generateReport(rateid);
      const { htmlUrl, pdfUrl } = getDocumentUrls(data);
      const route = getDocumentViewerRoute(rateid, {
        htmlUrl,
        pdfUrl,
        documentNumber: `PL-${rateid}`,
        documentType: "Pricelist",
      });
      navigate(route);
    } catch (error) {
      console.error("Print report failed:", error);
    } finally {
      setPrintingId(null);
    }
  };

  const {
    isLoading: isAllPricelistsLoading,
    data: { data: allpricelists, page: currentPage, totalPages } = [],
  } = useAllPricelists(filter);

  const handleVendorChange = (selectedOption) => {
    setSelectedVendor(selectedOption);
    setFilter((prev) => ({ ...prev, supid: selectedOption?.ps_Id || "" }));
  };

  const handleStatusChange = (selectedOption) => {
    setSelectedStatus(selectedOption);
    setFilter((prev) => ({ ...prev, ag_status: selectedOption?.value || "" }));
  };

  const filterBy = (e) => {
    const { name, value } = e.target;
    setFilter((prev) => ({ ...prev, [name]: value }));
  };

  const handlePageChange = (page) => {
    setFilter((prev) => ({ ...prev, page }));
  };

  const clearFilters = () => {
    setSelectedVendor(null);
    setSelectedStatus(null);
    setFilter(initialFilter);
  };

  const columns = useMemo(() => {
    const cols = [
      {
        key: "vendor",
        header: "Vendor Name",
        render: (pl) => pl.vendor?.name || "N/A",
      },
    ];

    if (isTrader) {
      cols.push({
        key: "typ",
        header: "Type",
        render: (pl) => typeLabels[pl.typ] || pl.typ,
      });
    }

    cols.push(
      {
        key: "cirdt",
        header: "Circular Date",
        render: (pl) => showDateInWords(pl.cirdt),
      },
      {
        key: "effrctivedt",
        header: "Effective Date",
        render: (pl) => showDateInWords(pl.effrctivedt),
      },
      { key: "pl_remark", header: "Remark" },
      {
        key: "ag_status",
        header: "Status",
        render: (pl) =>
          pl.ag_status === 1 ? (
            <CheckIcon className="h-5 w-5 text-green-500" aria-hidden="true" />
          ) : (
            <XMarkIcon className="h-5 w-5 text-red-500" aria-hidden="true" />
          ),
      },
      { key: "actions", isActions: true }
    );

    return cols;
  }, [isTrader]);

  return (
    <MasterPage
      title="Pricelist Master"
      headerActions={
        <AddButton onClick={() => openSetModal()}>Add pricelist</AddButton>
      }
      filters={
        <>
          <FilterRow>
            <FilterCell>
              <SelectVendors
                value={selectedVendor}
                onChange={handleVendorChange}
                label="Vendor Name"
                placeholder="Select Vendor"
              />
            </FilterCell>
            <FilterCell>
              <Select
                value={selectedStatus}
                onChange={handleStatusChange}
                options={statusOptions}
                label="Status"
                placeholder="Select Status"
              />
            </FilterCell>
            <FilterCell>
              <Input
                type="date"
                value={filter.from_cirdt}
                name="from_cirdt"
                onChange={filterBy}
                label="From Circular Date"
                placeholder="From Circular Date"
              />
            </FilterCell>
            <FilterCell>
              <Input
                type="date"
                value={filter.to_cirdt}
                name="to_cirdt"
                onChange={filterBy}
                label="To Circular Date"
                placeholder="To Circular Date"
              />
            </FilterCell>
          </FilterRow>
          <FilterRow>
            <FilterCell>
              <Input
                type="date"
                value={filter.from_effrctivedt}
                name="from_effrctivedt"
                onChange={filterBy}
                label="From Effective Date"
                placeholder="From Effective Date"
              />
            </FilterCell>
            <FilterCell>
              <Input
                type="date"
                value={filter.to_effrctivedt}
                name="to_effrctivedt"
                onChange={filterBy}
                label="To Effective Date"
                placeholder="To Effective Date"
              />
            </FilterCell>
            <ClearFiltersButton onClick={clearFilters} />
          </FilterRow>
        </>
      }
      columns={columns}
      data={allpricelists}
      isLoading={isAllPricelistsLoading}
      rowKey="rateid"
      renderActions={(pricelist) => (
        <>
          <EditAction
            onClick={() => openSetModal(pricelist.rateid)}
            variant="secondary"
          />
          <ActionButton
            onClick={() => handlePrintReport(pricelist.rateid)}
            variant="primary"
            title="Print Report"
            disabled={printingId === pricelist.rateid}
          >
            <PrinterIcon className="h-4 w-4" />
          </ActionButton>
          <DeleteAction onClick={() => setDeleteItemId(pricelist.rateid)} />
        </>
      )}
      extraModals={
        <DeletePricelist
          id={deleteItemId}
          open={!!deleteItemId}
          onClose={() => setDeleteItemId(null)}
        />
      }
      pagination={{
        currentPage,
        totalPages,
        onPageChange: handlePageChange,
      }}
    />
  );
}
